import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  FormErrors,
  SelectBooleanInput,
  NumberInput,
  TextInput
} from 'blueprint-form';
import { Row, Col } from '../../../../grid';
import { CardBlock } from '../../../organisms/card';

interface EpxVitalsSettingsData {
  alerts: boolean;
  thresholds: {
    temperature: {
      min: number;
      max: number;
    };
    bloodPressure: {
      systolicBP: {
        min: number;
        max: number;
      };
      diastolicBP: {
        min: number;
        max: number;
      };
    };
    heartRate: {
      min: number;
      max: number;
    };
    oxygen: {
      min: number;
    };
  };
}

const DefaultSettings: EpxVitalsSettingsData = {
  alerts: true,
  thresholds: {
    temperature: {
      min: 96.8,
      max: 100.4
    },
    bloodPressure: {
      systolicBP: {
        min: 90,
        max: 180
      },
      diastolicBP: {
        min: 60,
        max: 110
      }
    },
    heartRate: {
      min: 60,
      max: 100
    },
    oxygen: {
      min: 93
    }
  }
};

interface EpxVitalsSettingsProps {
  setForm?: (form: FormInstance<EpxVitalsSettingsData>) => void;
  onSubmit?: (data: EpxVitalsSettingsData) => void;
  disabled?: boolean;
}

const EpxVitalsSettings = ({
  setForm,
  onSubmit: _onSubmit,
  disabled
}: EpxVitalsSettingsProps): JSX.Element => {
  const form = new FormInstance<EpxVitalsSettingsData>(DefaultSettings);
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const validate = (data: EpxVitalsSettingsData): FormErrors => {
    const errors: any = {};
    if (!(parseFloat(String(data.thresholds.temperature.min)) >= 90)) {
      if (!errors.thresholds) {
        errors.thresholds = {};
      }
      if (!errors.thresholds.temperature) {
        errors.thresholds.temperature = {};
      }
      errors.thresholds.temperature.min =
        'The temperature threshold is not valid!';
    }
    if (!(parseFloat(String(data.thresholds.temperature.max)) >= 90)) {
      if (!errors.thresholds) {
        errors.thresholds = {};
      }
      if (!errors.thresholds.temperature) {
        errors.thresholds.temperature = {};
      }
      errors.thresholds.temperature.max =
        'The temperature threshold is not valid!';
    }
    return errors;
  };
  const onSubmit = (data: EpxVitalsSettingsData) => {
    const _data: EpxVitalsSettingsData = {
      alerts: data.alerts,
      thresholds: {
        ...data.thresholds,
        temperature: {
          min: parseFloat(String(data.thresholds.temperature.min)),
          max: parseFloat(String(data.thresholds.temperature.max))
        }
      }
    };
    _onSubmit(_data);
  };
  return (
    <CardBlock bare>
      <Form form={form} validate={validate} onSubmit={onSubmit}>
        <CardBlock>
          <Row>
            <Col sm={12}>
              <SelectBooleanInput
                label="Enable Alerts for Patient"
                name="alerts"
                fill
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <TextInput
                label="Temperature - Min (F)"
                name="thresholds.temperature.min"
                required
                fill
                disabled={disabled}
              />
            </Col>
            <Col sm={12}>
              <TextInput
                label="Temperature - Max (F)"
                name="thresholds.temperature.max"
                required
                fill
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <NumberInput
                label="Systolic BP - Min (mmHg)"
                name="thresholds.bloodPressure.systolicBP.min"
                min={1}
                required
                fill
                disabled={disabled}
              />
            </Col>
            <Col sm={12}>
              <NumberInput
                label="Systolic BP - Max (mmHg)"
                name="thresholds.bloodPressure.systolicBP.max"
                min={1}
                required
                fill
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <NumberInput
                label="Diastolic BP - Min (mmHg)"
                name="thresholds.bloodPressure.diastolicBP.min"
                min={1}
                required
                fill
                disabled={disabled}
              />
            </Col>
            <Col sm={12}>
              <NumberInput
                label="Diastolic BP - Max (mmHg)"
                name="thresholds.bloodPressure.diastolicBP.max"
                min={1}
                required
                fill
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <NumberInput
                label="Heart Rate - Min (BPM)"
                name="thresholds.heartRate.min"
                min={1}
                required
                fill
                disabled={disabled}
              />
            </Col>
            <Col sm={12}>
              <NumberInput
                label="Heart Rate - Max (BPM)"
                min={1}
                name="thresholds.heartRate.max"
                required
                fill
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <NumberInput
                label="SpO2 - Min"
                name="thresholds.oxygen.min"
                min={50}
                required
                fill
                disabled={disabled}
              />
            </Col>
          </Row>
        </CardBlock>
      </Form>
    </CardBlock>
  );
};

export default EpxVitalsSettings;
